/**
 * Markdown rendering and editing. Markdown renders through
 * `@mouja/domain/markdown` — the same sanitiser and parser the server stores
 * through — into React elements; there is no `dangerouslySetInnerHTML` here
 * or anywhere in the product. The editor toggles textarea and preview.
 */

import { useId, useMemo, useState, type ReactNode } from 'react';

import {
  parseMarkdown,
  sanitizeMarkdown,
  type MarkdownBlock,
  type MarkdownInline,
} from '@mouja/domain/markdown';

import { Button } from '../ui.js';
import { useWorkspaceCopy } from './use-workspace-copy.js';

function InlineNodes({
  nodes,
}: {
  readonly nodes: readonly MarkdownInline[];
}): ReactNode {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case 'text':
        return node.text;
      case 'strong':
        return (
          <strong key={index}>
            <InlineNodes nodes={node.children} />
          </strong>
        );
      case 'emphasis':
        return (
          <em key={index}>
            <InlineNodes nodes={node.children} />
          </em>
        );
      case 'code':
        // Code is an identifier by nature: isolated LTR under RTL.
        return (
          <code key={index} className="mj-ltr" dir="ltr">
            {node.text}
          </code>
        );
      case 'link':
        return (
          <a key={index} href={node.href} target="_blank" rel="noopener noreferrer">
            <InlineNodes nodes={node.children} />
          </a>
        );
      default:
        return null;
    }
  });
}

function BlockNode({ block }: { readonly block: MarkdownBlock }): ReactNode {
  switch (block.kind) {
    case 'heading': {
      // Document headings sit *under* the page's own h1/h2, so the deepest
      // Markdown heading maps to h3–h5 and the outline stays honest.
      const Level = (['h3', 'h4', 'h5'] as const)[block.level - 1] ?? 'h5';
      return (
        <Level>
          <InlineNodes nodes={block.children} />
        </Level>
      );
    }
    case 'paragraph':
      return (
        <p>
          <InlineNodes nodes={block.children} />
        </p>
      );
    case 'list': {
      const isChecklist = block.items.some((item) => item.checked !== null);
      const items = block.items.map((item, index) => (
        <li
          key={index}
          style={
            item.checked === null
              ? undefined
              : { display: 'flex', alignItems: 'baseline', gap: 8 }
          }
        >
          {item.checked === null ? null : (
            // State, not a control: a stored checklist reads as done or not
            // done, and flipping it is an edit to the document.
            <input type="checkbox" checked={item.checked} disabled readOnly />
          )}
          <span>
            <InlineNodes nodes={item.children} />
          </span>
        </li>
      ));
      const style = isChecklist
        ? { listStyle: 'none', paddingInlineStart: 0 }
        : undefined;
      return block.ordered ? (
        <ol style={style}>{items}</ol>
      ) : (
        <ul style={style}>{items}</ul>
      );
    }
    case 'quote':
      return (
        <blockquote>
          <InlineNodes nodes={block.children} />
        </blockquote>
      );
    case 'code':
      return (
        <pre className="mj-ltr" dir="ltr">
          <code>{block.text}</code>
        </pre>
      );
    case 'rule':
      return <hr />;
    case 'table':
      // The table scrolls inside its own container so a wide decision
      // matrix never forces the page sideways.
      return (
        <div style={{ overflowX: 'auto' }}>
          <table className="mj-table">
            <thead>
              <tr>
                {block.header.map((cell, index) => (
                  <th key={index} scope="col">
                    <InlineNodes nodes={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>
                      <InlineNodes nodes={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

/**
 * Sanitised Markdown, rendered as elements. Safe on raw input by design.
 * `maxLength` must match the storing surface's bound — the default is the
 * task bound, and Data Room documents pass their larger one.
 */
export function MarkdownView({
  source,
  maxLength,
}: {
  readonly source: string;
  readonly maxLength?: number | undefined;
}): ReactNode {
  const blocks = useMemo(
    () => parseMarkdown(sanitizeMarkdown(source, maxLength).text),
    [source, maxLength],
  );
  return (
    <div className="mj-markdown">
      {blocks.map((block, index) => (
        <BlockNode key={index} block={block} />
      ))}
    </div>
  );
}

/**
 * The Markdown editor: a textarea and a preview, toggled, never side by side.
 *
 * The toggle uses `aria-pressed` and the preview is a polite live region, per
 * the handoff. The preview renders through the same sanitiser the server
 * stores through, so it can never promise formatting the record will not keep.
 */
export function MarkdownField({
  label,
  hint,
  error,
  value,
  onChange,
  rows = 10,
  maxLength,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | null;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly rows?: number;
  readonly maxLength?: number;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const [previewing, setPreviewing] = useState(false);
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const toggle = (
    <div style={{ display: 'inline-flex', gap: 4, marginBottom: 6 }}>
      <Button
        size="sm"
        variant={previewing ? 'quiet' : 'outline'}
        aria-pressed={!previewing}
        onClick={() => {
          setPreviewing(false);
        }}
      >
        {copy.markdown.edit}
      </Button>
      <Button
        size="sm"
        variant={previewing ? 'outline' : 'quiet'}
        aria-pressed={previewing}
        onClick={() => {
          setPreviewing(true);
        }}
      >
        {copy.markdown.preview}
      </Button>
    </div>
  );

  return (
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <label
          htmlFor={id}
          style={{
            font: 'var(--type-caption)',
            letterSpacing: 'var(--type-caption-tracking)',
            color: 'var(--text-secondary)',
          }}
        >
          {label}
        </label>
        {toggle}
      </div>

      {previewing ? (
        <div
          aria-live="polite"
          aria-label={copy.markdown.previewLabel}
          style={{
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--radius-input)',
            padding: '12px 16px',
            minHeight: rows * 24,
            background: 'var(--surface-card)',
          }}
        >
          {value.trim().length === 0 ? (
            <p style={{ color: 'var(--text-muted)', font: 'var(--type-body-md)' }}>
              {copy.markdown.previewEmpty}
            </p>
          ) : (
            <MarkdownView source={value} maxLength={maxLength} />
          )}
        </div>
      ) : (
        <textarea
          id={id}
          value={value}
          rows={rows}
          maxLength={maxLength}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
            undefined
          }
          style={{
            width: '100%',
            minHeight: rows * 24,
            padding: '12px 16px',
            borderRadius: 'var(--radius-input)',
            border: `1px solid ${error ? 'var(--status-error-fg)' : 'var(--border-input)'}`,
            background: 'var(--surface-card)',
            color: 'var(--text-primary)',
            font: 'var(--type-body-md)',
            lineHeight: 1.6,
            resize: 'vertical',
          }}
        />
      )}

      {hint ? (
        <p
          id={hintId}
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            marginTop: 4,
          }}
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          style={{
            font: 'var(--type-caption)',
            color: 'var(--status-error-fg)',
            marginTop: 4,
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
